import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getLocalMember } from '../members';
import { Input } from '../ui';
import { addMessage, setIsKeyboardOpen } from './action';
import { fetchErrorData } from '../../../modules/API/API';
import { getIsKeyboardOpen } from './functions';
import { closeTestModal } from '../../test/action';
import { getIsTestOpenModal } from '../../test';
import { isIpadLikeBrowser, KEYBOARD_CLOSED_DIFF, resetStaleScrollTops } from './staleScrollReset';
const ChatInputContainer = React.memo(() => {
    const isMimac = APP.config?.name === 'mimac';
    const smallMobile = screen.width <= 505 || screen.height <= 505;
    // iPad Chrome/Edge/Firefox(모두 iOS WKWebView): 오토필 액세서리 바 공간이 키보드 닫힘 후에도
    // 반납되지 않아 하단 검은 띠가 남는 브라우저 이슈가 있다(웹으로 해소 불가). 페이지 로드 시
    // autoFocus 로 키보드/바가 자동으로 떠 그 띠가 바로·지속적으로 생기는 것을 막기 위해,
    // 이 브라우저에서만 autoFocus 를 끈다(사용자가 실제로 입력창을 탭할 때만 키보드가 뜨도록).
    const ua = window.navigator.userAgent;
    const isIpadThirdPartyBrowser = (/iPad/.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1)) &&
        /CriOS|EdgiOS|FxiOS/i.test(ua);
    const inputRef = useRef(null);
    const sendingRef = useRef(false); // 동기적 중복 전송 차단 (IME Enter 가 같은 틱에 두 번 발생하는 문제)
    const dispatch = useDispatch();
    const local = useSelector((state) => getLocalMember(state));
    const initialViewportHeight = useRef(window.visualViewport?.height || window.innerHeight);
    const isKeyboardOpen = useSelector((state) => getIsKeyboardOpen(state));
    const isTestModalOpen = useSelector((state) => getIsTestOpenModal(state));
    const chatDisabled = local?.chat_disabled;
    const [chatInput, setChatInput] = useState('');
    const [canSendMessage, setCanSendMessage] = useState(true);
    const [isComposing, setIsComposing] = useState(false);
    const onCompositionStart = () => {
        setIsComposing(true);
    };
    const onCompositionEnd = () => {
        setIsComposing(false);
    };
    const onSendMessage = () => {
        if (sendingRef.current)
            return; // 같은 틱에 두 번 호출돼도 1회만 전송 (중복 방지)
        toast.dismiss('chat-input-delay');
        if (inputRef.current) {
            const message = inputRef.current.value.trim();
            if (message === '' || !canSendMessage)
                return;
            sendingRef.current = true;
            setTimeout(() => { sendingRef.current = false; }, 500);
            fetchErrorData({
                message: 'message',
                data: message,
            });
            dispatch(addMessage(message, local, true));
            setChatInput('');
            setCanSendMessage(false);
        }
    };
    const onKeyDown = (e) => {
        e.stopPropagation();
        // IME 조합 중 Enter 무시. React state(isComposing) 갱신 지연 대비 이벤트별 native 플래그도 확인.
        if (isComposing || e.nativeEvent?.isComposing)
            return;
        if ((e.key === 'Enter' || e.key === 'NumpadEnter') && !e.shiftKey) {
            onSendMessage();
            e.preventDefault();
        }
        if (e.key === 'Space' && !e.shiftKey) {
            e.preventDefault();
        }
    };
    useEffect(() => {
        if (chatDisabled)
            setChatInput('');
    }, [chatDisabled]);
    // ★언마운트 시(X버튼/탭 전환 등 어떤 닫힘 경로든) 채팅 입력이 "실제 포커스일 때만" 명시 해제.
    // iOS 18(아이폰 16 계열)은 포커스된 요소가 DOM 에서 제거돼도 키보드+액세서리바를 내려주지
    // 않는 케이스가 있어(iOS17 이하의 자동 해제 동작이 깨짐), blur 없이 언마운트되면 키보드가
    // 유령처럼 잔존한다. useLayoutEffect cleanup 은 노드 detach "전"에 동기 실행되어 blur 가 유효.
    // - inputRef 한정 + activeElement 일치 판정: 다른 입력(테스트 주관식 등)이 포커스인 경우엔
    //   완전 no-op — 그 키보드를 내리면 안 되는 보호 시나리오(Room.web.tsx 트리거③ 가드) 무손상.
    // - ownerDocument 기준 판정: 듀얼모니터(별도 창)에서 opener 의 document 로 오판하지 않게.
    useLayoutEffect(() => {
        return () => {
            const el = inputRef.current;
            const doc = el?.ownerDocument;
            if (el && doc && doc.activeElement === el) {
                el.blur();
                dispatch(setIsKeyboardOpen(false));
            }
        };
    }, []);
    useEffect(() => {
        if (!canSendMessage) {
            const timer = setTimeout(() => {
                setCanSendMessage(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [canSendMessage]);
    useEffect(() => {
        if (!smallMobile)
            return;
        if (!isKeyboardOpen && isTestModalOpen)
            return;
        if (!window.visualViewport)
            return;
        let resizeTimeout;
        const handleViewportChange = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const currentHeight = window.visualViewport?.height ?? window.innerHeight;
                const heightDiff = initialViewportHeight.current - currentHeight;
                if (heightDiff > 200) {
                    dispatch(setIsKeyboardOpen(true));
                    dispatch(closeTestModal()); // Close test modal when keyboard opens
                }
                else if (heightDiff < 50) {
                    dispatch(setIsKeyboardOpen(false));
                }
            }, 100);
        };
        window.visualViewport.addEventListener('resize', handleViewportChange);
        return () => {
            clearTimeout(resizeTimeout);
            window.visualViewport?.removeEventListener('resize', handleViewportChange);
        };
    }, [dispatch, smallMobile, isTestModalOpen, isKeyboardOpen]);
    useEffect(() => {
        const handleOrientationChange = () => {
            if (inputRef.current) {
                inputRef.current.blur();
            }
            dispatch(setIsKeyboardOpen(false));
        };
        if (screen.orientation) {
            screen.orientation.addEventListener('change', handleOrientationChange);
            return () => {
                screen.orientation.removeEventListener('change', handleOrientationChange);
            };
        }
        else {
            window.addEventListener('orientationchange', handleOrientationChange);
            return () => {
                window.removeEventListener('orientationchange', handleOrientationChange);
            };
        }
    }, [dispatch, smallMobile]);
    useEffect(() => {
        if (!smallMobile)
            return;
        if (!isKeyboardOpen)
            return;
        setTimeout(() => {
            const chatBody = document.getElementById('chat_body_scroll');
            if (chatBody) {
                chatBody.scrollTop = chatBody.scrollHeight;
            }
        }, 200);
    }, [smallMobile, isKeyboardOpen]);
    const handleFocus = () => {
        if (smallMobile)
            dispatch(closeTestModal());
        setTimeout(() => {
            inputRef.current?.scrollIntoView({
                behavior: "instant",
                block: "end",
            });
        }, 100);
    };
    const handleBlur = () => {
        if (!smallMobile) {
            // ★ 보조 트리거 ② — iPad 전용 stale scrollTop 자가치유(주력은 Room.web.tsx 의
            // visualViewport resize 트리거 — iPad 키보드 접기 키는 blur 미발생이라 blur 만으론 부족).
            // UA 가 키보드 reveal 로 overflow:hidden 조상들에 밀어넣은 scrollTop 을 원복한다.
            // ★ 과거 실패(#root height/transform/--vh 변경 계열)와 달리 기하를 만들지 않고
            // 스크롤 오프셋 원복만 — 이미 0 이면 완전 no-op 이라 오발화 안전.
            // b62cde3f 와의 차이: window.scrollY 사전 게이트 없음(요소별 개별 판정),
            // blur 단독 의존 아님, instant 스크롤로 html smooth 회피.
            if (!isIpadLikeBrowser())
                return;
            // WKWebView 의 늦은 vv 정착 대비 250/650ms 두 시점 확인(중복 발화 no-op 안전).
            [250, 650].forEach(delay => {
                setTimeout(() => {
                    // 다른 입력으로 포커스가 이동했다면 키보드가 아직 떠 있음 — skip.
                    const activeElement = document.activeElement;
                    if (activeElement &&
                        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA'))
                        return;
                    // 키보드 열림 중(diff 큼) 리셋 금지 — 닫힘 판정(diff < 120)일 때만.
                    const vvH = window.visualViewport?.height ?? window.innerHeight;
                    if (window.innerHeight - vvH >= KEYBOARD_CLOSED_DIFF)
                        return;
                    // textarea ref 에서 조상 체인 시작(셀렉터 불필요 — chat_input 클래스는 래퍼 div).
                    resetStaleScrollTops(inputRef.current);
                }, delay);
            });
            return;
        }
        setTimeout(() => {
            const chatBody = document.getElementById('chat_body_scroll');
            if (chatBody) {
                chatBody.scrollTop = chatBody.scrollHeight;
            }
            const activeElement = document.activeElement;
            const isInputFocused = activeElement &&
                (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA');
            if (!isInputFocused) {
                dispatch(setIsKeyboardOpen(false));
            }
        }, 200);
    };
    return (React.createElement(Input, { ref: inputRef, value: chatInput, className: `chat_input ${chatDisabled ? "chat_disabled" : ""} ${smallMobile ? "" : "not_mobile"}`, autoFocus: !chatDisabled && !isIpadThirdPartyBrowser, textarea: true, minRows: isMimac && smallMobile ? 2 : 3, maxRows: isMimac && smallMobile ? 2 : 3, onChange: setChatInput, onKeyPress: canSendMessage ? onKeyDown : undefined, onCompositionStart: onCompositionStart, onCompositionEnd: onCompositionEnd, onFocus: handleFocus, onBlur: handleBlur, disabled: chatDisabled, placeholder: chatDisabled ? "chat.chatLock" : "" }));
});
export default ChatInputContainer;
